import { useLazyQuery } from '@apollo/client';
import { ResultOf } from '@graphql-typed-document-node/core';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Avatar,
  AvatarFallback,
  Combobox,
  ComboboxDropdown,
  ComboboxSelectButton,
  Icon,
  Stack,
  tv,
} from '@eluve/components';

import { UserComboboxContent } from './UserComboboxContent';
import { UserOption } from './UserOption';
import { searchTenantUsersQuery, tenantUserOptionFragment } from './operations';

interface TenantUserSelectorProps {
  disabled?: boolean;
  selectedUserId?: string;
  tenantId: string;
  onUserSelected: (userId: string) => void;
}

const selectUserVariant = tv({
  base: 'w-full px-4 hover:bg-bg-quaternary',
  variants: {
    disabled: {
      true: 'pointer-events-none',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

export const TenantUserSelector: React.FC<TenantUserSelectorProps> = ({
  tenantId,
  selectedUserId,
  onUserSelected,
  disabled = false,
}) => {
  const [searchResults, setSearchResults] = useState<
    ResultOf<typeof tenantUserOptionFragment>[]
  >([]);
  const [searchTenantUsers, { loading }] = useLazyQuery(searchTenantUsersQuery);

  const selectedUser = useCompleteFragment({
    fragment: tenantUserOptionFragment,
    key: {
      id: selectedUserId ?? '',
    },
    strict: false,
  });

  const handleUserSelected = (userId: string) => {
    onUserSelected(userId);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchResults([]);

        if (!query || query.length < 2) {
          return;
        }

        const filterConditions = [
          { tenantId: { _eq: tenantId } },
          {
            _or: [
              { user: { email: { _ilike: `%${query}%` } } },
              { user: { firstName: { _ilike: `%${query}%` } } },
              { user: { lastName: { _ilike: `%${query}%` } } },
            ],
          },
        ];

        searchTenantUsers({
          variables: {
            filter: { _and: filterConditions },
            limit: 25,
            orderBy: [{ user: { firstName: 'ASC' } }],
          },
        }).then((result) => {
          const users = (result.data?.tenantUsers ?? [])
            .map((tenantUser) => tenantUser.user)
            .filter((user): user is NonNullable<typeof user> => user !== null);
          setSearchResults(users);
        });
      }, 250),
    [searchTenantUsers, tenantId],
  );

  useEffect(
    function cancelDebounceOnUnmount() {
      return () => debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton className={selectUserVariant({ disabled })}>
        {selectedUser ? (
          <UserOption {...selectedUser} />
        ) : (
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            <Avatar size="lg">
              <AvatarFallback>
                <Icon name="User" />
              </AvatarFallback>
            </Avatar>
            <span className="text-text-secondary">Select User</span>
          </Stack>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown
        className="pointer-events-auto"
        searchPlaceholder="Search by name or email"
        shouldFilter={false}
      >
        <UserComboboxContent
          selectedUser={selectedUser}
          searchResults={searchResults}
          loading={loading}
          handleUserSelected={handleUserSelected}
          tenantId={tenantId}
          debouncedSearch={debouncedSearch}
          setSearchResults={setSearchResults}
        />
      </ComboboxDropdown>
    </Combobox>
  );
};
